import { Subtasks, TasksInitialState } from 'constants/interfaces'
import keys from 'constants/keys'
import { actionTypes } from 'redux/actions/tasks_v2'

const initialState: TasksInitialState = {
  isLoading: false,
  isInsightsLoading: false,
  tasks: [],
  ownerTasks: [],
  subtasks: {},
  pollSubtasks: {},
  privatePollSubtasks: {},
  imageSubtasks: {},
  videoSubtasks: {},
  linkSubtasks: {},
  questionSubtasks: {},
  userResults: {},
  allResults: {},
  deepDiveInsights: {
    contexts: {},
    insights: {},
  },
  insights: {},
  reportPeriod: null,
  taskFromInsights: null,
  taskOverviewModal: null,
  taskAudienceMap: {
    users: {},
    groups: {},
    tags: {},
  },
  filter: {
    all: true,
    groups: [],
    tags: [],
    users: [],
  },
  allResultsLoading: false,
  error: null,
}

export const tasksV2Reducer = (state = initialState, action: any) => {
  switch (action.type) {
    case actionTypes.SET_IS_LOADING:
      return { ...state, isLoading: action.payload }
    case actionTypes.SET_IS_INSIGHTS_LOADING:
      return { ...state, isInsightsLoading: action.payload }
    case actionTypes.SET_SUBTASKS:
      const subtasks = action.payload as Subtasks

      const pollSubtasks = Object.fromEntries(
        Object.entries(subtasks)
          .map(([key, valueArray]) => [
            key,
            valueArray.filter((value) => value.type === keys.SUBTASK_TYPES.POLL_SUBTASK),
          ])
          .filter(([key, valueArray]) => valueArray.length > 0)
      )
      const privatePollSubtasks = Object.fromEntries(
        Object.entries(subtasks)
          .map(([key, valueArray]) => [
            key,
            valueArray.filter((value) => value.type === keys.SUBTASK_TYPES.PRIVATE_POLL),
          ])
          .filter(([key, valueArray]) => valueArray.length > 0)
      )

      const imageSubtasks = Object.fromEntries(
        Object.entries(subtasks)
          .map(([key, valueArray]) => [
            key,
            valueArray.filter((value) => value.type === keys.SUBTASK_TYPES.IMAGE_SUBTASK),
          ])
          .filter(([key, valueArray]) => valueArray.length > 0)
      )

      const videoSubtasks = Object.fromEntries(
        Object.entries(subtasks)
          .map(([key, valueArray]) => [
            key,
            valueArray.filter((value) => value.type === keys.SUBTASK_TYPES.VIDEO_SUBTASKS),
          ])
          .filter(([key, valueArray]) => valueArray.length > 0)
      )
      const linkSubtasks = Object.fromEntries(
        Object.entries(subtasks)
          .map(([key, valueArray]) => [
            key,
            valueArray.filter((value) => value.type === keys.SUBTASK_TYPES.LINK_SUBTASK),
          ])
          .filter(([key, valueArray]) => valueArray.length > 0)
      )
      const questionSubtasks = Object.fromEntries(
        Object.entries(subtasks)
          .map(([key, valueArray]) => [
            key,
            valueArray.filter((value) => value.type === keys.SUBTASK_TYPES.OPEN_QUESTION),
          ])
          .filter(([key, valueArray]) => valueArray.length > 0)
      )

      return {
        ...state,
        subtasks: subtasks,
        pollSubtasks: pollSubtasks,
        privatePollSubtasks: privatePollSubtasks,
        imageSubtasks: imageSubtasks,
        videoSubtasks: videoSubtasks,
        linkSubtasks: linkSubtasks,
        questionSubtasks: questionSubtasks,
      }

    case actionTypes.SET_USER_RESULTS:
      return { ...state, userResults: action.payload }
    case actionTypes.SET_ALL_RESULTS:
      return { ...state, allResults: action.payload }
    case actionTypes.SET_TASKS:
      return { ...state, tasks: action.payload }
    case actionTypes.SET_OWNER_TASKS:
      return { ...state, ownerTasks: action.payload }
    case actionTypes.SET_TASKS_DEEP_DIVE_INSIGHTS:
      return { ...state, deepDiveInsights: action.payload }
    case actionTypes.SET_TASKS_INSIGHTS:
      return {
        ...state,
        insights: action.payload,
        reportPeriod: action.reportPeriod === undefined ? state.reportPeriod : action.reportPeriod,
      }
    case actionTypes.PATCH_INSIGHTS_RESULT: {
      const { tid, result } = action.payload
      const task = state.insights?.[tid]
      if (!task) return state
      const existing = task.results || []
      const idx = existing.findIndex(
        (r: any) =>
          r.st_id === result.st_id && r.context_id === result.context_id && r.recurrence_ts === result.recurrence_ts
      )
      const results = idx !== -1 ? Object.assign([...existing], { [idx]: result }) : [...existing, result]
      return { ...state, insights: { ...state.insights, [tid]: { ...task, results } } }
    }
    case actionTypes.PATCH_DEEP_DIVE_RESULT: {
      const { tid, contextId, patch } = action.payload
      const deepDive = state.deepDiveInsights
      // Only one task's deep dive is cached at a time and the overview modal can be open on another
      // task — patching then would corrupt an unrelated task's rows.
      if (!deepDive || deepDive.tid !== tid) return state
      const entry = deepDive.insights?.[contextId]
      // No row to patch: this is a first submission, which `PATCH_INSIGHTS_RESULT` already covers
      // (a subtask absent from the deep dive does not overwrite the insights row).
      if (!entry?.items?.some((item: any) => item.st_id === patch.st_id)) return state
      return {
        ...state,
        deepDiveInsights: {
          ...deepDive,
          insights: {
            ...deepDive.insights,
            [contextId]: {
              ...entry,
              items: entry.items.map((item: any) =>
                item.st_id === patch.st_id ? { ...item, status: patch.status } : item
              ),
            },
          },
        },
      }
    }
    case actionTypes.SET_FULL_TASK_FROM_INSIGHTS:
      return {
        ...state,
        taskFromInsights: action.payload,
      }
    case actionTypes.SET_TASK_OVERVIEW_MODAL:
      return {
        ...state,
        taskOverviewModal: action.payload,
      }
    case actionTypes.PATCH_TASK_OVERVIEW_MODAL:
      return {
        ...state,
        taskOverviewModal: state.taskOverviewModal ? { ...state.taskOverviewModal, ...action.payload } : null,
      }
    case actionTypes.SET_TASK_AUDIENCE_MAP:
      return { ...state, taskAudienceMap: action.payload }
    case actionTypes.SET_FILTER:
      return { ...state, filter: action.payload }
    case actionTypes.SET_ALL_RESULTS_LOADING:
      return { ...state, allResultsLoading: action.payload }
    case actionTypes.SET_ERROR:
      return { ...state, error: action.payload }
    case actionTypes.CLEAR_ERROR:
      return { ...state, error: null }
    default:
      return state
  }
}
